import React from 'react';
import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const RolePermissionsSection = ({
  formData,
  errors,
  onRoleChange,
  rolePermissions,
  originalUser
}) => {
  const roleOptions = [
    { value: 'admin', label: 'Administrator' },
    { value: 'manager', label: 'Manager' },
    { value: 'viewer', label: 'Viewer' }
  ];

  const hasRoleChanged = formData?.role !== originalUser?.role;
  const currentPermissions = rolePermissions?.[formData?.role] || [];

  // Role hierarchy levels for UI indication
  const roleHierarchy = {
    admin: { level: 3, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
    manager: { level: 2, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
    viewer: { level: 1, color: 'text-green-600', bg: 'bg-green-50 border-green-200' }
  };

  const roleInfo = roleHierarchy?.[formData?.role] || roleHierarchy?.viewer;

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          Role & Permissions
        </h2>
        <p className="text-sm text-muted-foreground">
          Manage user role assignments and access permissions
        </p>
      </div>
      <div className="space-y-6">
        {/* Role Selection */}
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-foreground mb-2">
            User Role <span className="text-red-500">*</span>
            {hasRoleChanged && (
              <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                Modified
              </span>
            )}
          </label>
          <Select
            id="role"
            options={roleOptions}
            value={formData?.role || ''}
            onChange={(value) => onRoleChange?.(value)}
            placeholder="Select user role"
            className={cn(
              errors?.role && 'border-red-300 focus:border-red-500 focus:ring-red-200',
              hasRoleChanged && 'border-orange-300 focus:border-orange-500 focus:ring-orange-200'
            )}
          />
          {errors?.role && (
            <p className="mt-1 text-xs text-red-600">
              {errors?.role}
            </p>
          )}
          {hasRoleChanged && !errors?.role && (
            <p className="mt-1 text-xs text-orange-600">
              Role will be changed from "{originalUser?.role}" to "{formData?.role}"
            </p>
          )}
        </div>

        {/* Current Role Information */}
        <div className={cn("rounded-lg p-4 border", roleInfo?.bg)}>
          <div className="flex items-center space-x-2 mb-3">
            <div className={cn("w-2 h-2 rounded-full", roleInfo?.color?.replace('text-', 'bg-'))}></div>
            <h3 className={cn("font-medium capitalize", roleInfo?.color)}>
              {formData?.role} Role
            </h3>
            <span className="text-xs text-muted-foreground">
              Access Level {roleInfo?.level}/3
            </span>
          </div>
          
          <div className="text-sm text-muted-foreground mb-3">
            This role provides the following permissions:
          </div>
          
          <ul className="space-y-1">
            {currentPermissions?.map((permission, index) => (
              <li key={index} className="flex items-center space-x-2 text-sm">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{permission}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Role Change Impact Warning */}
        {hasRoleChanged && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">Role Change Impact</h4>
                <div className="text-sm text-yellow-700 space-y-1">
                  <p>Changing this user's role will:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Immediately update their access permissions</li>
                    <li>Affect what features and data they can access</li>
                    <li>May require them to log in again to refresh permissions</li>
                    {originalUser?.role === 'admin' && formData?.role !== 'admin' && (
                      <li className="text-red-600 font-medium">Remove administrative privileges</li>
                    )}
                  </ul>
                  <p className="mt-2 font-medium">
                    You'll be asked to confirm this change before it's applied.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Self-Edit Warning */}
        {originalUser?.role === 'admin' && originalUser?.id === 1 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Security Notice</p>
                <p>
                  As a system administrator, you cannot remove your own admin privileges to prevent 
                  accidental lockout from the system. Another administrator must make role changes to your account.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolePermissionsSection;